import { ProjectFinancialsContractType } from 'api/project';
import { TranslationKey, useTranslation } from 'hooks/use-translation';

interface Props {
  financialContractType: ProjectFinancialsContractType;
}

interface ReturnProps {
  todayLabel: string;
  projectBudgetLabel: string;
  projectBudgetRemainingLabel: string;
  projectHoursLabel: string;
  projectHoursRemainingLabel: string;
  revenueLabel: string;
  estimatedRevenueLabel: string;
  actualRevenueLabel: string;
  costLabel: string;
  estimatedCostLabel: string;
  actualCostLabel: string;
  profitLabel: string;
  estimatedProfitLabel: string;
  actualProfitLabel: string;
  marginLabel: string;
  estimatedMarginLabel: string;
  actualMarginLabel: string;
  actualLabel: string;
  estimatedLabel: string;
  trackedLabel: string;
  allocatedLabel: string;
  timeAllocatedLabel: string;
  timeTrackedLabel: string;
  billableTimeTrackedLabel: string;
  nonBillableTimeTrackedLabel: string;
  budgetTableTitle: string;
  hoursTableTitle: string;
}

export const useProjectFinancialsLabels = ({
  financialContractType,
}: Props): ReturnProps => {
  const todayLabel = useTranslation('today');
  const projectBudgetLabel = useTranslation(
    `project.financials.${financialContractType}.project_budget` as TranslationKey,
  );
  const projectBudgetRemainingLabel = useTranslation(
    `project.financials.${financialContractType}.project_budget_remaining` as TranslationKey,
  );
  const projectHoursLabel = useTranslation(
    `project.financials.${financialContractType}.project_hours` as TranslationKey,
  );
  const projectHoursRemainingLabel = useTranslation(
    `project.financials.${financialContractType}.project_hours_remaining` as TranslationKey,
  );
  const revenueLabel = useTranslation(
    `project.financials.${financialContractType}.revenue` as TranslationKey,
  );
  const estimatedRevenueLabel = useTranslation(
    `project.financials.${financialContractType}.estimated_revenue` as TranslationKey,
  );
  const actualRevenueLabel = useTranslation(
    `project.financials.${financialContractType}.actual_revenue` as TranslationKey,
  );
  const costLabel = useTranslation(
    `project.financials.${financialContractType}.cost` as TranslationKey,
  );
  const estimatedCostLabel = useTranslation(
    `project.financials.${financialContractType}.estimated_cost` as TranslationKey,
  );
  const actualCostLabel = useTranslation(
    `project.financials.${financialContractType}.actual_cost` as TranslationKey,
  );
  const profitLabel = useTranslation(
    `project.financials.${financialContractType}.profit` as TranslationKey,
  );
  const estimatedProfitLabel = useTranslation(
    `project.financials.${financialContractType}.estimated_profit` as TranslationKey,
  );
  const actualProfitLabel = useTranslation(
    `project.financials.${financialContractType}.actual_profit` as TranslationKey,
  );
  const marginLabel = useTranslation(
    `project.financials.${financialContractType}.margin` as TranslationKey,
  );
  const estimatedMarginLabel = useTranslation(
    `project.financials.${financialContractType}.estimated_margin` as TranslationKey,
  );
  const actualMarginLabel = useTranslation(
    `project.financials.${financialContractType}.actual_margin` as TranslationKey,
  );
  const actualLabel = useTranslation(
    `project.financials.${financialContractType}.actual` as TranslationKey,
  );
  const estimatedLabel = useTranslation(
    `project.financials.${financialContractType}.estimated` as TranslationKey,
  );
  const trackedLabel = useTranslation(
    `project.financials.${financialContractType}.tracked` as TranslationKey,
  );
  const allocatedLabel = useTranslation(
    `project.financials.${financialContractType}.allocated` as TranslationKey,
  );
  const timeAllocatedLabel = useTranslation(
    `project.financials.${financialContractType}.time_allocated` as TranslationKey,
  );
  const timeTrackedLabel = useTranslation(
    `project.financials.${financialContractType}.time_tracked` as TranslationKey,
  );
  const billableTimeTrackedLabel = useTranslation(
    `project.financials.${financialContractType}.billable_time_tracked` as TranslationKey,
  );
  const nonBillableTimeTrackedLabel = useTranslation(
    `project.financials.${financialContractType}.non_billable_time_tracked` as TranslationKey,
  );
  const budgetTableTitle = useTranslation(
    `project.financials.${financialContractType}.table.budget.title` as TranslationKey,
  );
  const hoursTableTitle = useTranslation(
    `project.financials.${financialContractType}.table.hours.title` as TranslationKey,
  );

  return {
    todayLabel,
    projectBudgetLabel,
    projectBudgetRemainingLabel,
    projectHoursLabel,
    projectHoursRemainingLabel,
    revenueLabel,
    estimatedRevenueLabel,
    actualRevenueLabel,
    costLabel,
    estimatedCostLabel,
    actualCostLabel,
    profitLabel,
    estimatedProfitLabel,
    actualProfitLabel,
    marginLabel,
    estimatedMarginLabel,
    actualMarginLabel,
    actualLabel,
    estimatedLabel,
    trackedLabel,
    allocatedLabel,
    timeAllocatedLabel,
    timeTrackedLabel,
    billableTimeTrackedLabel,
    nonBillableTimeTrackedLabel,
    budgetTableTitle,
    hoursTableTitle,
  };
};
